const BASE = process.env.PUBLIC_URL + '/mediapipe'
const MODULE_URL = BASE + '/vision_bundle.mjs'
const WASM_PATH = BASE + '/wasm'
const GESTURE_MODEL = process.env.PUBLIC_URL + '/models/gesture_recognizer.task'
const POSE_MODEL =
    process.env.PUBLIC_URL + '/models/pose_landmarker_lite.task'

// The MediaPipe ESM bundle uses modern syntax that CRA4 / webpack4 cannot parse.
// Load it natively in the browser at runtime instead of importing it through the
// bundler. `new Function` keeps webpack from statically analysing the import.
// eslint-disable-next-line no-new-func
const nativeImport = new Function('url', 'return import(url)')

// Import an ESM URL, working around servers that send .mjs as
// "application/octet-stream" (the browser refuses to run those as modules under
// strict MIME checking). If the direct import fails, fetch the source and import
// it through a Blob URL tagged text/javascript. The bundle is self-contained and
// resolves the wasm via an explicit path, so the Blob URL doesn't break it.
async function importEsm(url) {
    try {
        return await nativeImport(url)
    } catch (e) {
        const res = await fetch(url)
        if (!res.ok) throw e
        const code = await res.text()
        const blobUrl = URL.createObjectURL(
            new Blob([code], { type: 'text/javascript' })
        )
        try {
            return await nativeImport(blobUrl)
        } finally {
            URL.revokeObjectURL(blobUrl)
        }
    }
}

let modulePromise = null
let gesturePromise = null
let gestureImagePromise = null
let posePromise = null
let poseImagePromise = null
let lastVideoTimestamp = 0

function loadModule() {
    if (!modulePromise) {
        // The self-hosted MediaPipe ESM bundle is loaded natively (not through
        // webpack), so webpack's build-time `process.env` substitution never
        // runs and the bundle hits a bare `process` reference -> "process is
        // not defined". Provide a minimal global shim before importing it.
        if (typeof window !== 'undefined' && typeof window.process === 'undefined') {
            window.process = { env: {} }
        }
        modulePromise = importEsm(MODULE_URL).catch((e) => {
            modulePromise = null
            throw e
        })
    }
    return modulePromise
}

// MediaPipe VIDEO mode requires strictly increasing timestamps; every task
// shares this one monotonic counter.
export function nextVideoTimestamp(now) {
    const ts = Math.max(lastVideoTimestamp + 1, Math.round(now))
    lastVideoTimestamp = ts
    return ts
}

export function getGestureRecognizer() {
    if (!gesturePromise) {
        gesturePromise = loadModule()
            .then(async (mp) => {
                const fs = await mp.FilesetResolver.forVisionTasks(WASM_PATH)
                return mp.GestureRecognizer.createFromOptions(fs, {
                    baseOptions: {
                        modelAssetPath: GESTURE_MODEL,
                        delegate: 'GPU',
                    },
                    runningMode: 'VIDEO',
                    numHands: 2,
                    minHandDetectionConfidence: 0.5,
                    minTrackingConfidence: 0.5,
                })
            })
            .catch((e) => {
                gesturePromise = null
                throw e
            })
    }
    return gesturePromise
}

// IMAGE-mode recognizer for converting stored still training images to hand
// landmarks (the VIDEO recognizer above is for the live feed).
export function getGestureRecognizerImage() {
    if (!gestureImagePromise) {
        gestureImagePromise = loadModule()
            .then(async (mp) => {
                const fs = await mp.FilesetResolver.forVisionTasks(WASM_PATH)
                return mp.GestureRecognizer.createFromOptions(fs, {
                    baseOptions: {
                        modelAssetPath: GESTURE_MODEL,
                        delegate: 'GPU',
                    },
                    runningMode: 'IMAGE',
                    numHands: 1,
                })
            })
            .catch((e) => {
                gestureImagePromise = null
                throw e
            })
    }
    return gestureImagePromise
}

// MediaPipe Pose Landmarker (33 full-body landmarks). VIDEO mode for the live
// Execute feed; IMAGE mode for converting stored training frames to vectors.
export function getPoseLandmarker() {
    if (!posePromise) {
        posePromise = loadModule()
            .then(async (mp) => {
                const fs = await mp.FilesetResolver.forVisionTasks(WASM_PATH)
                return mp.PoseLandmarker.createFromOptions(fs, {
                    baseOptions: {
                        modelAssetPath: POSE_MODEL,
                        delegate: 'GPU',
                    },
                    runningMode: 'VIDEO',
                    numPoses: 1,
                })
            })
            .catch((e) => {
                posePromise = null
                throw e
            })
    }
    return posePromise
}

export function getPoseLandmarkerImage() {
    if (!poseImagePromise) {
        poseImagePromise = loadModule()
            .then(async (mp) => {
                const fs = await mp.FilesetResolver.forVisionTasks(WASM_PATH)
                return mp.PoseLandmarker.createFromOptions(fs, {
                    baseOptions: {
                        modelAssetPath: POSE_MODEL,
                        delegate: 'GPU',
                    },
                    runningMode: 'IMAGE',
                    numPoses: 1,
                })
            })
            .catch((e) => {
                poseImagePromise = null
                throw e
            })
    }
    return poseImagePromise
}

// Normalise 33 pose landmarks into a translation/scale-invariant vector: centre
// on the hip midpoint (landmarks 23,24), scale by the torso span, and drop the
// low-signal face points beyond the shoulders so the classifier keys on body
// posture. Returns a flat [x0,y0,x1,y1,...] vector (66 values).
export function normalizePoseLandmarks(landmarks) {
    if (!landmarks || landmarks.length < 33) return null
    const lh = landmarks[23]
    const rh = landmarks[24]
    const ls = landmarks[11]
    const rs = landmarks[12]
    if (!lh || !rh || !ls || !rs) return null
    const cx = (lh.x + rh.x) / 2
    const cy = (lh.y + rh.y) / 2
    const shoulderMidY = (ls.y + rs.y) / 2
    // Torso length (hip midpoint -> shoulder midpoint) as the scale reference.
    let scale = Math.hypot((ls.x + rs.x) / 2 - cx, shoulderMidY - cy)
    if (!scale || scale < 1e-3) scale = 1
    const vec = []
    for (const p of landmarks) {
        vec.push((p.x - cx) / scale, (p.y - cy) / scale)
    }
    return vec
}

// Largest detected pose -> { vector, landmarks }.
export function poseVectorFromResult(result) {
    const poses = result?.landmarks || []
    if (!poses.length) return null
    const landmarks = poses[0]
    return { vector: normalizePoseLandmarks(landmarks), landmarks }
}

// Turn 21 raw hand landmarks into a 42-dim feature vector that is invariant to
// position (wrist-centred), scale (divided by hand span) and handedness (left
// hands mirrored onto the right-hand canonical frame) — so a gesture trained
// with one hand still recognises the other. This is why landmarks beat a raw
// image CNN, which memorises background/skin/handedness instead.
export function normalizeHandLandmarks(landmarks, handedness) {
    if (!landmarks || landmarks.length < 21) return null
    const wrist = landmarks[0]
    const isLeft = handedness === 'Left'
    const pts = landmarks.map((p) => ({
        x: (p.x - wrist.x) * (isLeft ? -1 : 1),
        y: p.y - wrist.y,
    }))
    let maxD = 0
    for (const p of pts) maxD = Math.max(maxD, Math.hypot(p.x, p.y))
    maxD = maxD || 1
    const vec = []
    for (const p of pts) vec.push(p.x / maxD, p.y / maxD)
    return vec
}

// Pick the largest hand from a MediaPipe result and return its normalized
// feature vector plus raw landmarks (for drawing markers).
export function handVectorFromResult(result) {
    const hands = result?.landmarks || []
    if (!hands.length) return null
    let idx = 0
    let bestSpan = -1
    hands.forEach((lm, i) => {
        let minX = 1
        let maxX = 0
        let minY = 1
        let maxY = 0
        lm.forEach((p) => {
            if (p.x < minX) minX = p.x
            if (p.x > maxX) maxX = p.x
            if (p.y < minY) minY = p.y
            if (p.y > maxY) maxY = p.y
        })
        const span = (maxX - minX) * (maxY - minY)
        if (span > bestSpan) {
            bestSpan = span
            idx = i
        }
    })
    const landmarks = hands[idx]
    const handedness = result.handedness?.[idx]?.[0]?.categoryName
    return {
        vector: normalizeHandLandmarks(landmarks, handedness),
        landmarks,
        handedness,
    }
}
